import * as React from 'react';

import { once } from 'lodash';

import AdminSchoolSisSyncHeader from 'admin/rostering/components/AdminSchoolSisSyncHeader';
import SyncHoldCopy, { SyncHold } from 'admin/constants/SyncHoldCopy';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Copy from 'components/Copy';
import Heading from 'components/Heading';
import ModalTrigger from 'components/ModalTrigger';
import Text from 'components/Text';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';

import AdminSisSyncHoldViewResourceTable from 'admin/rostering/components/AdminSisSyncHoldViewResourceTable';
import AdminSisSyncHoldViewResumeModal from 'admin/rostering/components/AdminSisSyncHoldViewResumeModal';

import { AdminSchoolSisDashboard_organizations_sisSyncOverview_holdSummary as HoldSummary } from 'admin/rostering/queries/__generated__/AdminSchoolSisDashboard';

import './styles/AdminSisSyncHoldView.scss';

type Props = {
  orgUuid: string;
  holdSummary: HoldSummary | null;
  lastSyncTimestamp?: number;
  syncMethod: string;
};

export default class AdminSisSyncHoldView extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  renderHeader() {
    const subtext =
      'The most recent sync has been placed on hold. Review the following to resolve the issue and continue syncing.';

    return (
      <AdminSchoolSisSyncHeader
        subtext={subtext}
        status="on hold"
        lastSyncTimestamp={this.props.lastSyncTimestamp}
      />
    );
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.hold.view', {
      organization_uud: this.props.orgUuid,
    });
  });

  render() {
    const { orgUuid, syncMethod, holdSummary } = this.props;

    if (!holdSummary) {
      return <ErrorReporter error={'syncStatus is on_hold but holdSummary is null'} />;
    }

    const {
      hold,
      temporarilyIgnoreable,
      permanentlyIgnoreable,
      affectedResourcesSummary,
    } = holdSummary!;

    // TODO: Fix this typecast when AdminSchoolSisDashboard is converted to typescript
    const { reason, nextStep } = SyncHoldCopy[hold as SyncHold];

    return (
      <section className="AdminSisSyncHoldView">
        {this.renderHeader()}
        <Box textAlign="left">
          <BoxBody full className="AdminSisSyncHoldView-reason">
            <Heading level="2" levelStyle="3">
              <Text color="white">Hold Reason</Text>
            </Heading>
            <Copy>
              <Text color="white" size="medium">
                <Text element="span" weight="semibold" size="medium">
                  {reason.problem}
                </Text>{' '}
                {reason.explanation}
              </Text>
            </Copy>
          </BoxBody>
          <BoxBody divided className="AdminSisSyncHoldView-next">
            <Heading level="2" levelStyle="3">
              Next Step
            </Heading>
            <Copy>
              <Text size="medium">
                <p>{nextStep.intro}</p>
                {nextStep.bullets && (
                  <ul>
                    {nextStep.bullets.map(text => {
                      return (
                        <li>
                          <p>{text}</p>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {nextStep.footer && <p>{nextStep.footer}</p>}
              </Text>
            </Copy>
          </BoxBody>
          {affectedResourcesSummary.length !== 0 && (
            <AdminSisSyncHoldViewResourceTable
              orgUuid={orgUuid}
              affectedResources={affectedResourcesSummary}
            />
          )}
          <BoxFooter className="AdminSisSyncHoldView-footer">
            <Buttons align="left">
              <ModalTrigger
                modal={
                  <AdminSisSyncHoldViewResumeModal
                    holdKey={hold}
                    orgUuid={orgUuid}
                    syncMethod={syncMethod}
                  />
                }>
                <Button>I fixed it</Button>
              </ModalTrigger>
              {temporarilyIgnoreable && (
                <ModalTrigger
                  modal={
                    <AdminSisSyncHoldViewResumeModal
                      holdKey={hold}
                      orgUuid={orgUuid}
                      syncMethod={syncMethod}
                      permanentlyIgnoreable={permanentlyIgnoreable}
                      temporarilyIgnored
                    />
                  }>
                  <Button level="secondary">Ignore the hold and continue</Button>
                </ModalTrigger>
              )}
            </Buttons>
          </BoxFooter>
        </Box>
      </section>
    );
  }
}
